<template>
  <Component
    :is="el"
    :class="[
      'own-status w-full flex-row align-center',
      isTrigger ? 'justify-between' : 'justify-start',
      `own-status--${status}`,
      isTrigger && 'own-status--trigger',
      disabled && 'own-status--disabled',
    ]"
    :aria-disabled="disabled"
    :role="isTrigger ? 'button' : undefined"
    :tabindex="disabled ? -1 : undefined"
  >
    <span class="text-button-regular" v-text="text"></span>

    <PhCaretDown v-if="isTrigger" size="16" />
    <Component :is="icon" v-else-if="icon" size="20" />
  </Component>
</template>

<script setup lang="ts">
import { PhCaretDown } from '@phosphor-icons/vue'
import { type Component } from 'vue'

type StatusType = 'success' | 'error' | 'warning' | 'neutral'

interface Props {
  /** Use this to render a specific component */
  el?: string
  /** An optional icon for the badge */
  icon?: Component
  /** Is a trigger for an action menu */
  isTrigger?: boolean
  /** The status for the badge */
  status?: StatusType
  /** The text for the badge */
  text: string
  /** Disabled state for the status component */
  disabled?: boolean
}

withDefaults(defineProps<Props>(), {
  disabled: false,
  el: 'div',
  icon: undefined,
  isTrigger: false,
  status: 'neutral',
})
</script>

<style lang="scss">
@use '@/styles/globals';

@mixin define-color($color) {
  background: linear-gradient(
      0deg,
      rgba($color, 0.2) 0%,
      rgba($color, 0.2) 100%
    ),
    #fff;
  color: rgb($color);

  &:hover.own-status--trigger {
    background: linear-gradient(
        0deg,
        rgba(255, 255, 255, 0.5) 0%,
        rgba(255, 255, 255, 0.5) 100%
      ),
      linear-gradient(0deg, rgba($color, 0.2) 0%, rgba($color, 0.2) 100%), #fff;
    color: rgba($color, 0.5);
  }
}

.own-status {
  border-radius: 8px;
  padding: 8px 16px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  @include globals.control-shadow();

  &--neutral {
    @include define-color(var(--raw-text-color-secondary));
  }

  &--error {
    @include define-color(var(--raw-status-danger));
  }

  &--success {
    @include define-color(var(--raw-status-success));
  }

  &--warning {
    @include define-color(var(--raw-status-warning));
  }

  &--disabled {
    opacity: 0.5;
    pointer-events: none;

    &:hover.own-status--trigger {
      background: inherit;
      color: inherit;
    }
  }
}
</style>
